import { parseJwtPayload } from './authTokens';

export interface KeycloakConfig {
    ssoBaseUrl: string;
    clientId: string;
    redirectUri: string;
}

export interface TokenResponse {
    access_token: string;
    refresh_token?: string;
    id_token: string;
    expires_in: number;
    refresh_expires_in?: number;
}

export type SsoIdentity = {
    name: string;
    email: string;
};

type KeycloakAuthAttempt = {
    state: string;
    codeVerifier: string;
    nonce: string;
};

const AUTH_ATTEMPT_STORAGE_KEY = 'rid.keycloakAuthAttempt';
const PKCE_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

function getBaseOidcUrl(config: KeycloakConfig): string {
    const realmUrl = config.ssoBaseUrl.replace(/\/+$/, '');
    return `${realmUrl}/protocol/openid-connect`;
}

export function getSsoIdentityFromIdToken(idToken: string | null | undefined): SsoIdentity | null {
    if (!idToken) return null;

    const payload = parseJwtPayload(idToken);
    if (!payload) return null;

    const name =
        payload.name ||
        [payload.given_name, payload.family_name].filter(Boolean).join(' ') ||
        payload.preferred_username ||
        '';
    const email = payload.email || '';

    return name || email ? { name, email } : null;
}

function generateRandomString(length = 43): string {
    const values = new Uint32Array(length);
    crypto.getRandomValues(values);
    return Array.from(values, (value) => PKCE_CHARACTERS[value % PKCE_CHARACTERS.length]).join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

function clearCallbackParameters(): void {
    const url = new URL(window.location.href);
    ['code', 'state', 'session_state', 'iss', 'error', 'error_description'].forEach((parameter) => {
        url.searchParams.delete(parameter);
    });
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
}

export function clearKeycloakLoginAttempt(): void {
    sessionStorage.removeItem(AUTH_ATTEMPT_STORAGE_KEY);
}

export async function beginKeycloakAuthorization(
    config: KeycloakConfig,
    options: { forceLogin?: boolean } = {},
): Promise<void> {
    const codeVerifier = generateRandomString();
    const state = generateRandomString(32);
    const nonce = generateRandomString(32);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const attempt: KeycloakAuthAttempt = { state, codeVerifier, nonce };

    sessionStorage.setItem(AUTH_ATTEMPT_STORAGE_KEY, JSON.stringify(attempt));

    const authUrl = new URL(`${getBaseOidcUrl(config)}/auth`);
    authUrl.searchParams.set('client_id', config.clientId);
    authUrl.searchParams.set('redirect_uri', config.redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', 'openid profile email offline_access metrc metrc.retailid.export webservices');
    authUrl.searchParams.set('state', state);
    authUrl.searchParams.set('nonce', nonce);
    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');
    if (options.forceLogin) {
        // Ignore any existing Keycloak browser session so the user signs in with their own account.
        authUrl.searchParams.set('prompt', 'login');
    }

    window.location.assign(authUrl.toString());
}

export async function handleKeycloakCallback(config: KeycloakConfig): Promise<TokenResponse | null> {
    const authorization = readKeycloakAuthorizationResponse();
    if (!authorization) return null;

    const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        code: authorization.code,
        code_verifier: authorization.codeVerifier,
    });
    const response = await fetch(`${getBaseOidcUrl(config)}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
    });

    if (!response.ok) {
        const details = await response.text();
        throw new Error(`Token exchange failed (${response.status}): ${details || response.statusText}`);
    }

    const tokens = await response.json() as TokenResponse;
    const idTokenPayload = parseJwtPayload(tokens.id_token);
    if (!idTokenPayload || idTokenPayload.nonce !== authorization.nonce) {
        throw new Error('Invalid ID token nonce. Please start authentication again.');
    }

    return tokens;
}

export type KeycloakAuthorizationResponse = {
    code: string;
    codeVerifier: string;
    nonce: string;
};

// Checks the callback against this browser's own sign-in attempt (state) without exchanging the code. Confidential-client
// markets (RID-3173) send the result to the API, which exchanges the code and checks the nonce. Returns null without a code.
export function readKeycloakAuthorizationResponse(): KeycloakAuthorizationResponse | null {
    const callbackParameters = new URLSearchParams(window.location.search);
    const error = callbackParameters.get('error');
    if (error) {
        clearKeycloakLoginAttempt();
        throw new Error(callbackParameters.get('error_description') || error);
    }

    const code = callbackParameters.get('code');
    const state = callbackParameters.get('state');
    if (!code || !state) return null;

    const rawAttempt = sessionStorage.getItem(AUTH_ATTEMPT_STORAGE_KEY);
    clearKeycloakLoginAttempt();

    if (!rawAttempt) {
        throw new Error('Missing Keycloak login attempt. Please start authentication again.');
    }

    let attempt: KeycloakAuthAttempt;
    try {
        attempt = JSON.parse(rawAttempt) as KeycloakAuthAttempt;
    } catch {
        throw new Error('Invalid Keycloak login attempt. Please start authentication again.');
    }

    if (state !== attempt.state || !attempt.codeVerifier || !attempt.nonce) {
        throw new Error('Invalid authentication state. Please start authentication again.');
    }

    clearCallbackParameters();
    return { code, codeVerifier: attempt.codeVerifier, nonce: attempt.nonce };
}

// Previous stateful-session design, intentionally inactive for later review.
// The organization-market flow does not retain frontend tokens, refresh them,
// test their validity, or terminate the user's broader Keycloak browser session.
//
// private tokenData: TokenResponse | null = null;
// private tokenExpiryTime = 0;
//
// public async refreshToken(): Promise<TokenResponse> { ... }
// public async getValidToken(minValiditySeconds = 30): Promise<string> { ... }
// private setTokenData(data: TokenResponse): void { ... }
// public logout(): void { ... }
